/**
 * Footer — final CTA band + tidy link columns. The CTA band reuses the accent
 * gradient so the page closes on the same warm note it opened with.
 */
import { useNavigate } from 'react-router-dom';
import { PawPrint } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { scrollToId } from './scroll-to';

const COLUMNS: { title: string; links: { label: string; id?: string; route?: string }[] }[] = [
  {
    title: 'Product',
    links: [
      { label: 'Services', id: 'services' },
      { label: 'How it works', id: 'how-it-works' },
      { label: 'For vendors', id: 'audience' },
    ],
  },
  {
    title: 'Get started',
    links: [
      { label: 'Pet Owner console', route: '/user/login' },
      { label: 'Vendor console', route: '/login' },
      { label: 'Choose a console', route: '/choose' },
    ],
  },
  {
    /*
     * The deletion page has to be reachable from a public, linkable place — the store
     * listing points at it, and somebody who has already uninstalled the app has nowhere
     * else to start from.
     */
    title: 'Account',
    links: [{ label: 'Delete my account', route: '/request-account-deletion' }],
  },
];

export function LandingFooter() {
  const navigate = useNavigate();

  return (
    <footer className="px-5 pb-10">
      <div className="mx-auto w-full max-w-6xl">
        {/* Final CTA band */}
        <div className="relative overflow-hidden rounded-card accent-gradient px-8 py-12 text-center shadow-clay-lg sm:px-12 sm:py-16">
          <div aria-hidden className="pointer-events-none absolute inset-0 opacity-20">
            <PawPrint className="absolute left-8 top-8 h-16 w-16 -rotate-12 text-white" />
            <PawPrint className="absolute bottom-6 right-10 h-24 w-24 rotate-12 text-white" />
          </div>
          <h2 className="relative font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            Ready to give your pet the best care?
          </h2>
          <p className="relative mx-auto mt-3 max-w-xl text-lg text-white/90">
            Book a friendly demo or jump straight in — your pets will thank you.
          </p>
          <div className="relative mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              size="lg"
              className="bg-white text-base text-primary shadow-clay hover:bg-white/90"
              onClick={() => navigate('/choose')}
              data-testid="footer-get-started"
            >
              Get started
            </Button>
            <Button
              size="lg"
              variant="ghost"
              className="text-base text-white hover:bg-white/15"
              onClick={() => navigate('/choose')}
            >
              Sign in
            </Button>
          </div>
        </div>

        {/* Links */}
        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-button accent-gradient text-white shadow-clay-sm">
                <PawPrint className="h-5 w-5" />
              </span>
              <span className="font-heading text-lg font-bold tracking-tight">Pet Marketplace</span>
            </div>
            <p className="mt-3 max-w-xs text-sm text-muted-foreground">
              Every pet service, one happy place. Made with love for pets and the people who adore
              them.
            </p>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">
                {col.title}
              </h3>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <button
                      type="button"
                      onClick={() => (link.route ? navigate(link.route) : scrollToId(link.id!))}
                      className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 border-t-2 border-brand-outline-variant/20 pt-6 text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} Pet Marketplace. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
